// src/utils/inquiriesXlsx.js
// ============================================================================
// XLSX Import / Export for Inquiries (Area Statement + Scenarios + Offers)
// Uses exceljs (browser-compatible).
//
// CANONICAL STORAGE UNIT: sq.ft.
//   All area values are stored internally in sq.ft. regardless of the UI
//   toggle (ft²/m²).
//
// EXPORT/IMPORT UNITS:
//   Export writes areas (and per-area rates) in the unit currently selected
//   in the UI toggle, so the workbook matches what the user is looking at.
//   Every unit-bearing column header is suffixed with the unit it holds
//   ("Indoor (m²)", "Rate (₹/m²)", …), and import reads those headers to
//   decide whether to convert back to canonical sq.ft. Workbooks exported
//   before m² support always carry ft² headers, so they still import
//   correctly. Fees stay in ₹ in both units (area × rate is unit-invariant).
//
// WHY FORMULAS ARE NOT TRUSTED ON IMPORT:
//   The XLSX may be edited in Excel/Sheets and formulas could be stale,
//   broken, or manually overridden. On import we read only raw cell values
//   and re-derive all computed fields (carpet area, walls %, constructed
//   area, fee totals) using the same app logic functions.
//
// SHEET → APP JSON MAPPING:
//   Sheet "Area Statement" → areaStatements[activeVersion].groups[]
//   Sheet "Fee Scenarios"  → feeScenarios[].groupRates{}
//   Sheet "Offers"         → feeOffers[].groupRates{}
// ============================================================================
import ExcelJS from 'exceljs';
import { getExcelStyle } from './excelStyles';

// ── Constants (mirror InquiriesPage.jsx) ────────────────────────────
const SQFT_PER_M2 = 10.7639;

// ── BRIO brand colours ──────────────────────────────────────────────
const BRIO_GOLD = 'FF9F902A';
const BRIO_GOLD_LIGHT = 'FFF5F0D0';
const BRIO_HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD } };
const brioHeaderFont = () => ({ ...getExcelStyle('style1'), bold: true, color: { argb: 'FFFFFFFF' } });
const BRIO_SUBTOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD_LIGHT } };
const BRIO_CONSTRUCTED_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAE5B8' } };
const THIN_BORDER = { style: 'thin', color: { argb: 'FFD1D5DB' } };
const CELL_BORDER = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };

// ── Helpers ─────────────────────────────────────────────────────────
const colLetter = (n) => {
  let s = '';
  while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s;
};

const cellRef = (col, row) => `${colLetter(col)}${row}`;

const applyTableBorders = (ws, startRow, endRow, startCol, endCol) => {
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cell = ws.getCell(r, c);
      cell.border = CELL_BORDER;
    }
  }
};

const setRowHeight = (ws, row, h) => { ws.getRow(row).height = h; };

// ── Unit helpers ────────────────────────────────────────────────────
// Canonical storage is sq.ft.; the workbook is written in the unit the
// user currently has selected in the UI toggle.
const sqftToM2 = (v) => (parseFloat(v) || 0) / SQFT_PER_M2;
const m2ToSqft = (v) => (parseFloat(v) || 0) * SQFT_PER_M2;

// Rates are per unit area, so they scale the opposite way to areas:
// ₹/m² = ₹/ft² × SQFT_PER_M2.
const ratePerSqftToPerM2 = (v) => (parseFloat(v) || 0) * SQFT_PER_M2;
const ratePerM2ToPerSqft = (v) => (parseFloat(v) || 0) / SQFT_PER_M2;

/**
 * Detect the area unit a sheet's values are written in, from its column
 * headers. Returns 'm2' only when a header explicitly says m²; anything
 * else (including workbooks predating m² export) falls back to 'sqft'.
 */
const detectUnitFromHeaders = (ws, headerRow, cols) => {
  const row = ws.getRow(headerRow);
  for (const c of cols) {
    const h = cellStr(row.getCell(c)).toLowerCase();
    if (h.includes('m²') || h.includes('m2')) return 'm2';
  }
  return 'sqft';
};

// ── EXPORT ──────────────────────────────────────────────────────────

/**
 * Export inquiry data to an XLSX workbook buffer.
 *
 * @param {Object} params
 * @param {string} params.inquiryName  - display name for the inquiry
 * @param {Object} params.activeAreaVersion - the active area statement version object
 * @param {Array}  params.scenarios    - feeScenarios array
 * @param {Array}  params.offers       - feeOffers array
 * @param {string} params.areaUnit     - current UI unit ('sqft' or 'm2') for display hint
 * @param {Array}  params.sheets       - which sheets to include: ['area','scenarios','offers']
 * @returns {Promise<ArrayBuffer>}
 */
export async function exportInquiryToXlsx({
  inquiryName = 'Inquiry',
  activeAreaVersion,
  scenarios = [],
  offers = [],
  areaUnit = 'sqft',
  sheets = ['area', 'scenarios', 'offers'],
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BRIO Atlas';
  wb.created = new Date();

  const groups = activeAreaVersion?.groups || [];
  const isM2 = areaUnit === 'm2';
  const unitLabel = isM2 ? 'm²' : 'ft²';

  // Values are written in the UI's unit; formulas operate on the written
  // cells, so every number that feeds a formula must be converted too.
  const outArea = (sqft) => (isM2 ? sqftToM2(sqft) : (parseFloat(sqft) || 0));
  const outRate = (perSqft) => (isM2 ? ratePerSqftToPerM2(perSqft) : (parseFloat(perSqft) || 0));
  // m² figures are ~10× smaller than ft², so keep a decimal (matches the UI).
  const AREA_FMT = isM2 ? '#,##0.0' : '#,##0';

  // ── Sheet 1: Area Statement ───────────────────────────────────────
  if (sheets.includes('area')) {
    const ws = wb.addWorksheet('Area Statement');

    // Column widths
    ws.columns = [
      { width: 6 },   // A: Group
      { width: 6 },   // B: Seq
      { width: 24 },  // C: Program
      { width: 11 },  // D: Indoor
      { width: 12 },  // E: Covered Outdoor
      { width: 11 },  // F: Non-Covered Outdoor
      { width: 11 },  // G: Row Total
      { width: 10 },  // H: W&C %
    ];

    // Metadata rows
    let r = 1;
    ws.getCell(r, 1).value = `Inquiry: ${inquiryName}`;
    ws.getCell(r, 1).font = { ...getExcelStyle('style2'), bold: true };
    ws.mergeCells(r, 1, r, 4);
    r++;
    ws.getCell(r, 1).value = `Area Statement Version: ${activeAreaVersion?.name || 'Version 1'}`;
    ws.getCell(r, 1).font = { ...getExcelStyle('style1'), italic: true };
    r++;
    ws.getCell(r, 1).value = `Unit Display: ${unitLabel}`;
    ws.getCell(r, 1).font = { size: 9, color: { argb: 'FF888888' } };
    ws.getCell(r, 3).value = `Values in: ${unitLabel}`;
    ws.getCell(r, 3).font = { size: 9, color: { argb: 'FF888888' } };
    r++;
    r++; // blank row

    // Header row
    const headerRow = r;
    const headers = ['Group', 'Seq', 'Program', `Indoor (${unitLabel})`, `Cvrd Outdoor (${unitLabel})`, `Non-Cvrd (${unitLabel})`, `Row Total (${unitLabel})`, 'W&C %'];
    headers.forEach((h, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = h;
      cell.fill = BRIO_HEADER_FILL;
      cell.font = brioHeaderFont();
      cell.alignment = { vertical: 'middle', horizontal: i >= 3 ? 'right' : 'left' };
    });
    setRowHeight(ws, r, 22);
    r++;

    // Freeze panes below header
    ws.views = [{ state: 'frozen', ySplit: headerRow, xSplit: 0 }];

    // Track constructed area cell refs for grand total
    const constructedCells = [];

    // Blank row between header and first group
    r++;

    // Per-group rows
    groups.forEach((grp) => {
      const subrows = grp.subrows || [];

      // Subcategory header row: "A" in col A, label in col B
      if (grp.label) {
        ws.getCell(r, 1).value = grp.code;
        ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 2).value = grp.label;
        ws.getCell(r, 2).font = { ...getExcelStyle('style1'), bold: true, italic: true };
        setRowHeight(ws, r, 18);
        r++;
      }

      const groupStartRow = r;
      // Subrows
      subrows.forEach((sr) => {
        ws.getCell(r, 1).value = grp.code;
        ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 2).value = sr.num || '';
        ws.getCell(r, 3).value = sr.label || '';
        const indoor = outArea(sr.indoor);
        const co = outArea(sr.covered_outdoor);
        const nco = outArea(sr.non_covered_outdoor);
        ws.getCell(r, 4).value = indoor || null;
        ws.getCell(r, 5).value = co || null;
        ws.getCell(r, 6).value = nco || null;
        // Row total formula: =D{r}+E{r}+F{r}
        ws.getCell(r, 7).value = { formula: `D${r}+E${r}+F${r}`, result: indoor + co + nco };
        // Right-align numeric columns
        for (let c = 4; c <= 7; c++) {
          ws.getCell(r, c).alignment = { horizontal: 'right' };
          ws.getCell(r, c).numFmt = AREA_FMT;
        }
        setRowHeight(ws, r, 16);
        r++;
      });

      const groupEndRow = r - 1;

      // ── Carpet area subtotal row ──────────────────────────────────
      ws.getCell(r, 1).value = grp.code;
      ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
      ws.getCell(r, 3).value = 'carpet area';
      ws.getCell(r, 3).font = { ...getExcelStyle('style1'), bold: true };

      // Indoor carpet = SUM of indoor column for this group's subrows
      const indoorRange = `D${groupStartRow}:D${groupEndRow}`;
      const coRange = `E${groupStartRow}:E${groupEndRow}`;
      const ncoRange = `F${groupStartRow}:F${groupEndRow}`;
      const totalRange = `G${groupStartRow}:G${groupEndRow}`;

      const indoorCarpetOut = outArea(subrows.reduce((s, sr) => s + (parseFloat(sr.indoor) || 0), 0));

      ws.getCell(r, 4).value = { formula: `SUM(${indoorRange})`, result: indoorCarpetOut };
      ws.getCell(r, 5).value = { formula: `SUM(${coRange})`, result: outArea(subrows.reduce((s, sr) => s + (parseFloat(sr.covered_outdoor) || 0), 0)) };
      ws.getCell(r, 6).value = { formula: `SUM(${ncoRange})`, result: outArea(subrows.reduce((s, sr) => s + (parseFloat(sr.non_covered_outdoor) || 0), 0)) };
      ws.getCell(r, 7).value = { formula: `SUM(${totalRange})`, result: outArea(subrows.reduce((s, sr) => s + (parseFloat(sr.indoor) || 0) + (parseFloat(sr.covered_outdoor) || 0) + (parseFloat(sr.non_covered_outdoor) || 0), 0)) };

      for (let c = 4; c <= 7; c++) {
        ws.getCell(r, c).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, c).alignment = { horizontal: 'right' };
        ws.getCell(r, c).numFmt = AREA_FMT;
        ws.getCell(r, c).fill = BRIO_SUBTOTAL_FILL;
      }
      ws.getCell(r, 3).fill = BRIO_SUBTOTAL_FILL;
      ws.getCell(r, 1).fill = BRIO_SUBTOTAL_FILL;
      ws.getCell(r, 2).fill = BRIO_SUBTOTAL_FILL;

      const carpetRow = r;
      setRowHeight(ws, r, 18);
      r++;

      // ── Walls and circulation row ─────────────────────────────────
      ws.getCell(r, 3).value = 'walls and circulation';
      ws.getCell(r, 3).font = getExcelStyle('style1');
      const wcPct = parseFloat(grp.wallsCirculationPct) || 0;
      // W&C % in column H
      ws.getCell(r, 8).value = wcPct / 100;  // store as decimal for Excel
      ws.getCell(r, 8).numFmt = '0.0%';
      ws.getCell(r, 8).alignment = { horizontal: 'right' };
      // Computed value: indoor carpet × %  (walls applies ONLY to indoor)
      const indoorCarpetRef = `D${carpetRow}`;
      const pctRef = `H${r}`;
      ws.getCell(r, 7).value = { formula: `${indoorCarpetRef}*${pctRef}`, result: indoorCarpetOut * (wcPct / 100) };
      ws.getCell(r, 7).numFmt = AREA_FMT;
      ws.getCell(r, 7).alignment = { horizontal: 'right' };
      const wallsRow1 = r;
      setRowHeight(ws, r, 16);
      r++;

      // ── Constructed area row ──────────────────────────────────────
      ws.getCell(r, 1).value = grp.code;
      ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
      ws.getCell(r, 3).value = 'constructed area';
      ws.getCell(r, 3).font = { ...getExcelStyle('style1'), bold: true, color: { argb: BRIO_GOLD } };
      // constructed = carpet total + walls amount
      ws.getCell(r, 7).value = { formula: `G${carpetRow}+G${wallsRow1}` };
      ws.getCell(r, 7).numFmt = AREA_FMT;
      ws.getCell(r, 7).font = { ...getExcelStyle('style1'), bold: true, color: { argb: BRIO_GOLD } };
      ws.getCell(r, 7).alignment = { horizontal: 'right' };
      for (let c = 1; c <= 8; c++) {
        ws.getCell(r, c).fill = BRIO_CONSTRUCTED_FILL;
      }
      constructedCells.push(cellRef(7, r));
      setRowHeight(ws, r, 18);
      r++;

      // Blank separator
      r++;
    });

    // Grand total row
    if (constructedCells.length > 0) {
      ws.getCell(r, 3).value = `GRAND TOTAL (constructed, ${unitLabel})`;
      ws.getCell(r, 3).font = { ...getExcelStyle('style2'), bold: true, color: { argb: BRIO_GOLD } };
      ws.getCell(r, 7).value = { formula: constructedCells.join('+') };
      ws.getCell(r, 7).font = { ...getExcelStyle('style2'), bold: true, color: { argb: BRIO_GOLD } };
      ws.getCell(r, 7).numFmt = AREA_FMT;
      ws.getCell(r, 7).alignment = { horizontal: 'right' };
      for (let c = 1; c <= 8; c++) {
        ws.getCell(r, c).fill = BRIO_CONSTRUCTED_FILL;
        ws.getCell(r, c).border = { top: { style: 'medium', color: { argb: BRIO_GOLD } }, bottom: { style: 'medium', color: { argb: BRIO_GOLD } } };
      }
      r++;
    }

    // Apply borders to data area
    applyTableBorders(ws, headerRow, r - 1, 1, 8);
  }

  // ── Sheet 2: Fee Scenarios ────────────────────────────────────────
  if (sheets.includes('scenarios')) {
    const ws = wb.addWorksheet('Fee Scenarios');

    ws.columns = [
      { width: 22 },  // A: Scenario Name
      { width: 10 },  // B: Group Code
      { width: 22 },  // C: Group Label
      { width: 16 },  // D: Constructed Area
      { width: 16 },  // E: Rate
      { width: 18 },  // F: Fee
    ];

    let r = 1;
    ws.getCell(r, 1).value = `Inquiry: ${inquiryName}`;
    ws.getCell(r, 1).font = { ...getExcelStyle('style2'), bold: true };
    r++;
    ws.getCell(r, 1).value = 'Fee Scenarios — rates × constructed area';
    ws.getCell(r, 1).font = { ...getExcelStyle('style1'), italic: true };
    r++;
    r++;

    // Header
    const headerRow = r;
    const headers = ['Scenario', 'Group', 'Label', `Constructed Area (${unitLabel})`, `Rate (₹/${unitLabel})`, 'Fee (₹)'];
    headers.forEach((h, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = h;
      cell.fill = BRIO_HEADER_FILL;
      cell.font = brioHeaderFont();
      cell.alignment = { vertical: 'middle', horizontal: i >= 3 ? 'right' : 'left' };
    });
    setRowHeight(ws, r, 22);
    ws.views = [{ state: 'frozen', ySplit: headerRow, xSplit: 0 }];
    r++;

    // Data rows
    (scenarios || []).forEach((sc) => {
      const gRates = sc.groupRates || {};
      const scenarioStartRow = r;

      groups.forEach((grp) => {
        const constructedArea = getConstructedAreaForGroup(grp);
        const rateSqft = parseFloat(gRates[grp.id]) || 0;
        const fee = constructedArea * rateSqft;

        ws.getCell(r, 1).value = sc.name || '';
        ws.getCell(r, 2).value = grp.code;
        ws.getCell(r, 3).value = grp.label || '';
        ws.getCell(r, 4).value = isM2 ? outArea(constructedArea) : Math.round(constructedArea);
        ws.getCell(r, 4).numFmt = AREA_FMT;
        ws.getCell(r, 4).alignment = { horizontal: 'right' };
        ws.getCell(r, 5).value = outRate(rateSqft) || null;
        ws.getCell(r, 5).numFmt = '#,##0.00';
        ws.getCell(r, 5).alignment = { horizontal: 'right' };
        // Fee formula = D × E
        ws.getCell(r, 6).value = { formula: `D${r}*E${r}`, result: fee };
        ws.getCell(r, 6).numFmt = '#,##0';
        ws.getCell(r, 6).alignment = { horizontal: 'right' };
        setRowHeight(ws, r, 16);
        r++;
      });

      // Scenario total row
      if (groups.length > 0) {
        ws.getCell(r, 1).value = `${sc.name || 'Scenario'} — Total`;
        ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 4).value = { formula: `SUM(D${scenarioStartRow}:D${r - 1})` };
        ws.getCell(r, 4).numFmt = AREA_FMT;
        ws.getCell(r, 4).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 4).alignment = { horizontal: 'right' };
        ws.getCell(r, 6).value = { formula: `SUM(F${scenarioStartRow}:F${r - 1})` };
        ws.getCell(r, 6).numFmt = '#,##0';
        ws.getCell(r, 6).font = { ...getExcelStyle('style1'), bold: true, color: { argb: BRIO_GOLD } };
        ws.getCell(r, 6).alignment = { horizontal: 'right' };
        for (let c = 1; c <= 6; c++) { ws.getCell(r, c).fill = BRIO_SUBTOTAL_FILL; }
        setRowHeight(ws, r, 18);
        r++;
      }
      r++; // blank separator
    });

    applyTableBorders(ws, headerRow, r - 1, 1, 6);
  }

  // ── Sheet 3: Offers ───────────────────────────────────────────────
  if (sheets.includes('offers')) {
    const ws = wb.addWorksheet('Offers');

    ws.columns = [
      { width: 22 },  // A: Offer Label
      { width: 10 },  // B: Group Code
      { width: 22 },  // C: Group Label
      { width: 16 },  // D: Constructed Area
      { width: 16 },  // E: Rate
      { width: 18 },  // F: Fee
      { width: 14 },  // G: CC
      { width: 12 },  // H: Date
      { width: 22 },  // I: Notes
    ];

    let r = 1;
    ws.getCell(r, 1).value = `Inquiry: ${inquiryName}`;
    ws.getCell(r, 1).font = { ...getExcelStyle('style2'), bold: true };
    r++;
    ws.getCell(r, 1).value = 'Fee Offers — rates × constructed area';
    ws.getCell(r, 1).font = { ...getExcelStyle('style1'), italic: true };
    r++;
    r++;

    const headerRow = r;
    const headers = ['Offer', 'Group', 'Label', `Constructed Area (${unitLabel})`, `Rate (₹/${unitLabel})`, 'Fee (₹)', 'CC', 'Date', 'Notes'];
    headers.forEach((h, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = h;
      cell.fill = BRIO_HEADER_FILL;
      cell.font = brioHeaderFont();
      cell.alignment = { vertical: 'middle', horizontal: i >= 3 && i <= 5 ? 'right' : 'left' };
    });
    setRowHeight(ws, r, 22);
    ws.views = [{ state: 'frozen', ySplit: headerRow, xSplit: 0 }];
    r++;

    (offers || []).forEach((off) => {
      const gRates = off.groupRates || {};
      const offerStartRow = r;
      let isFirstRow = true;

      groups.forEach((grp) => {
        const constructedArea = getConstructedAreaForGroup(grp);
        const rateSqft = parseFloat(gRates[grp.id]) || 0;
        const fee = constructedArea * rateSqft;

        ws.getCell(r, 1).value = off.label || '';
        ws.getCell(r, 2).value = grp.code;
        ws.getCell(r, 3).value = grp.label || '';
        ws.getCell(r, 4).value = isM2 ? outArea(constructedArea) : Math.round(constructedArea);
        ws.getCell(r, 4).numFmt = AREA_FMT;
        ws.getCell(r, 4).alignment = { horizontal: 'right' };
        ws.getCell(r, 5).value = outRate(rateSqft) || null;
        ws.getCell(r, 5).numFmt = '#,##0.00';
        ws.getCell(r, 5).alignment = { horizontal: 'right' };
        ws.getCell(r, 6).value = { formula: `D${r}*E${r}`, result: fee };
        ws.getCell(r, 6).numFmt = '#,##0';
        ws.getCell(r, 6).alignment = { horizontal: 'right' };

        // Offer metadata on first row only
        if (isFirstRow) {
          ws.getCell(r, 7).value = off.cc || '';
          ws.getCell(r, 8).value = off.date || '';
          ws.getCell(r, 9).value = off.notes || '';
          isFirstRow = false;
        }
        setRowHeight(ws, r, 16);
        r++;
      });

      // Offer total
      if (groups.length > 0) {
        ws.getCell(r, 1).value = `${off.label || 'Offer'} — Total`;
        ws.getCell(r, 1).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 4).value = { formula: `SUM(D${offerStartRow}:D${r - 1})` };
        ws.getCell(r, 4).numFmt = AREA_FMT;
        ws.getCell(r, 4).font = { ...getExcelStyle('style1'), bold: true };
        ws.getCell(r, 4).alignment = { horizontal: 'right' };
        ws.getCell(r, 6).value = { formula: `SUM(F${offerStartRow}:F${r - 1})` };
        ws.getCell(r, 6).numFmt = '#,##0';
        ws.getCell(r, 6).font = { ...getExcelStyle('style1'), bold: true, color: { argb: BRIO_GOLD } };
        ws.getCell(r, 6).alignment = { horizontal: 'right' };
        for (let c = 1; c <= 9; c++) { ws.getCell(r, c).fill = BRIO_SUBTOTAL_FILL; }
        setRowHeight(ws, r, 18);
        r++;
      }
      r++;
    });

    applyTableBorders(ws, headerRow, r - 1, 1, 9);
  }

  // Write to buffer
  const buffer = await wb.xlsx.writeBuffer();
  return buffer;
}

/**
 * Compute constructed area for a single group.
 * Mirrors getGroupConstructedArea from InquiriesPage.jsx.
 * walls & circ applies ONLY to indoor carpet.
 */
function getConstructedAreaForGroup(grp) {
  const subrows = grp.subrows || [];
  let totalCarpet = 0, indoorCarpet = 0;
  subrows.forEach(r => {
    const indoor = parseFloat(r.indoor) || 0;
    const co = parseFloat(r.covered_outdoor) || 0;
    const nco = parseFloat(r.non_covered_outdoor) || 0;
    totalCarpet += indoor + co + nco;
    indoorCarpet += indoor;
  });
  const pct = parseFloat(grp.wallsCirculationPct) || 0;
  const wallsAmount = indoorCarpet * (pct / 100);
  return totalCarpet + wallsAmount;
}


// ── IMPORT ──────────────────────────────────────────────────────────

/**
 * Import inquiry data from an XLSX file.
 *
 * Returns { success, data, errors }
 *   data.areaVersion  - area statement version object (groups with subrows)
 *   data.scenarios    - array of scenario objects with groupRates
 *   data.offers       - array of offer objects with groupRates
 *
 * IMPORTANT: We do NOT trust Excel formulas. All derived values
 *   (carpet area, walls amounts, constructed area, fee totals)
 *   are recomputed from raw cell values after parsing.
 *
 * @param {File} file - .xlsx File object from <input type="file">
 * @returns {Promise<{success: boolean, data?: Object, errors?: string[]}>}
 */
export async function importInquiryFromXlsx(file) {
  const errors = [];

  try {
    const arrayBuffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(arrayBuffer);

    const result = {};

    // ── Parse Area Statement sheet ──────────────────────────────────
    const areaSheet = wb.getWorksheet('Area Statement');
    if (areaSheet) {
      result.areaVersion = parseAreaStatementSheet(areaSheet, errors);
    } else {
      errors.push('Sheet "Area Statement" not found — area data will not be imported.');
    }

    // ── Parse Fee Scenarios sheet ───────────────────────────────────
    const scenariosSheet = wb.getWorksheet('Fee Scenarios');
    if (scenariosSheet) {
      result.scenarios = parseScenariosSheet(scenariosSheet, result.areaVersion?.groups || [], errors);
    } else {
      errors.push('Sheet "Fee Scenarios" not found — scenarios will not be imported.');
    }

    // ── Parse Offers sheet ──────────────────────────────────────────
    const offersSheet = wb.getWorksheet('Offers');
    if (offersSheet) {
      result.offers = parseOffersSheet(offersSheet, result.areaVersion?.groups || [], errors);
    } else {
      errors.push('Sheet "Offers" not found — offers will not be imported.');
    }

    // If all sheets missing, that's a hard error
    if (!areaSheet && !scenariosSheet && !offersSheet) {
      return { success: false, errors: ['No recognized sheets found. Expected: "Area Statement", "Fee Scenarios", "Offers".'] };
    }

    // If there are errors but some data was parsed, return partial
    if (errors.length > 0 && !result.areaVersion && !result.scenarios && !result.offers) {
      return { success: false, errors };
    }

    return { success: true, data: result, errors: errors.length > 0 ? errors : undefined };
  } catch (err) {
    return { success: false, errors: [`Failed to read XLSX file: ${err.message}`] };
  }
}


// ── Sheet parsers ───────────────────────────────────────────────────

/**
 * Get raw value from a cell, unwrapping formula results.
 */
function cellVal(cell) {
  if (!cell || cell.value === null || cell.value === undefined) return null;
  const v = cell.value;
  // exceljs formula object: { formula, result }
  if (v && typeof v === 'object' && 'result' in v) return v.result;
  return v;
}

function cellNum(cell) {
  const v = cellVal(cell);
  if (v === null || v === undefined || v === '') return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function cellStr(cell) {
  const v = cellVal(cell);
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/**
 * Parse Area Statement sheet into a version object with groups[].
 * We read raw values only and recompute derived fields.
 *
 * Row structure per group:
 *   subrow data rows (Group, Seq, Program, Indoor, CvrdOutdr, NonCvrd)
 *   carpet area row (label "carpet area" in col C)
 *   walls and circulation row (label "walls and circulation" in col C, % in col H)
 *   blank row
 *   constructed area row (label "constructed area" in col C)
 *
 * We identify groups by:
 *   - data rows: col A has group code, col B has seq, col C has program name
 *   - summary rows: col C contains "carpet area", "walls and circulation", "constructed area"
 */
function parseAreaStatementSheet(ws, errors) {
  // Find header row (contains "Group" in col A)
  let headerRow = 0;
  ws.eachRow((row, rowNum) => {
    if (headerRow > 0) return;
    const a = cellStr(row.getCell(1));
    if (a.toLowerCase() === 'group') headerRow = rowNum;
  });

  if (headerRow === 0) {
    errors.push('Area Statement: Could not find header row (expected "Group" in column A).');
    return null;
  }

  // Validate headers
  const expectedHeaders = ['group', 'seq', 'program'];
  const actualHeaders = [1, 2, 3].map(c => cellStr(ws.getRow(headerRow).getCell(c)).toLowerCase());
  for (const eh of expectedHeaders) {
    if (!actualHeaders.some(h => h.includes(eh))) {
      errors.push(`Area Statement: Missing expected header "${eh}".`);
    }
  }

  // Which unit are the area columns written in? Headers are the source of
  // truth — workbooks exported before m² support always say ft², and their
  // values really are ft².
  const sheetUnit = detectUnitFromHeaders(ws, headerRow, [4, 5, 6, 7]);
  const toCanonicalArea = (v) => (sheetUnit === 'm2' ? m2ToSqft(v) : (parseFloat(v) || 0));

  // Read version name from metadata
  let versionName = 'Imported Version';
  for (let r = 1; r < headerRow; r++) {
    const val = cellStr(ws.getRow(r).getCell(1));
    if (val.startsWith('Area Statement Version:')) {
      versionName = val.replace('Area Statement Version:', '').trim();
    }
  }

  // Parse data rows
  const groupsMap = new Map(); // code → { code, label, subrows, wallsCirculationPct }
  const groupOrder = [];
  let currentGroupCode = null;

  const totalRows = ws.rowCount;
  for (let r = headerRow + 1; r <= totalRows; r++) {
    const row = ws.getRow(r);
    const rawA = cellStr(row.getCell(1));
    const colB = cellStr(row.getCell(2));
    const colC = cellStr(row.getCell(3));
    const colCLower = colC.toLowerCase();

    // Parse group code and optional subcategory label from col A
    // Supports both formats:
    //   New: dedicated header row with code in A, label in B (no program/data in C-F)
    //   Legacy: "A – Main Areas" encoded in col A
    let colA = rawA;
    let colALabel = '';
    if (rawA.includes(' – ')) {
      const parts = rawA.split(' – ');
      colA = parts[0].trim();
      colALabel = parts.slice(1).join(' – ').trim();
    }

    // Detect subcategory header row: group code in A, label text in B, no program in C, no numeric data
    if (colA && colB && !colC && cellNum(row.getCell(4)) === 0 && cellNum(row.getCell(5)) === 0 && cellNum(row.getCell(6)) === 0) {
      currentGroupCode = colA;
      if (!groupsMap.has(colA)) {
        groupsMap.set(colA, { code: colA, label: colB, subrows: [], wallsCirculationPct: 0 });
        groupOrder.push(colA);
      } else if (!groupsMap.get(colA).label) {
        groupsMap.get(colA).label = colB;
      }
      continue;
    }

    // Skip grand total row
    if (colCLower.includes('grand total')) continue;

    // Detect summary rows by col C label
    if (colCLower === 'carpet area') {
      // carpet area is derived — skip (we'll recompute)
      continue;
    }

    if (colCLower === 'walls and circulation') {
      // Read W&C % from col H
      const pctCell = row.getCell(8);
      let pctVal = cellNum(pctCell);
      // If stored as decimal (e.g. 0.10 for 10%), convert to percentage number
      if (pctVal > 0 && pctVal < 1) pctVal = pctVal * 100;
      if (currentGroupCode && groupsMap.has(currentGroupCode)) {
        groupsMap.get(currentGroupCode).wallsCirculationPct = pctVal;
      }
      continue;
    }

    if (colCLower === 'constructed area' || colCLower === '') {
      // Derived rows — skip
      continue;
    }

    // Data row: must have a group code or be continuation of current group
    const groupCode = colA || currentGroupCode;
    if (!groupCode) continue;

    // Skip if this looks like a total row
    if (colCLower.includes('total')) continue;

    currentGroupCode = groupCode;

    if (!groupsMap.has(groupCode)) {
      groupsMap.set(groupCode, {
        code: groupCode,
        label: colALabel || '',
        subrows: [],
        wallsCirculationPct: 0,
      });
      groupOrder.push(groupCode);
    } else if (colALabel && !groupsMap.get(groupCode).label) {
      // Update label from legacy "A – Label" format
      groupsMap.get(groupCode).label = colALabel;
    }

    const grp = groupsMap.get(groupCode);
    const indoor = toCanonicalArea(cellNum(row.getCell(4)));
    const coveredOutdoor = toCanonicalArea(cellNum(row.getCell(5)));
    const nonCovered = toCanonicalArea(cellNum(row.getCell(6)));

    // Only add if it has a program name or numeric data
    if (colC || indoor > 0 || coveredOutdoor > 0 || nonCovered > 0) {
      grp.subrows.push({
        num: colB || String(grp.subrows.length + 1),
        label: colC,
        indoor: indoor || '',
        covered_outdoor: coveredOutdoor || '',
        non_covered_outdoor: nonCovered || '',
      });
    }
  }

  // Build groups array with IDs
  const groups = groupOrder.map(code => {
    const g = groupsMap.get(code);
    return {
      id: `ag_imp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      code: g.code,
      label: g.label,
      collapsed: false,
      wallsCirculationPct: g.wallsCirculationPct || '',
      subrows: g.subrows.map((sr, i) => ({
        id: `ar_imp_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
        ...sr,
      })),
    };
  });

  if (groups.length === 0) {
    errors.push('Area Statement: No data rows found.');
    return null;
  }

  return {
    id: `av_imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: versionName,
    createdAt: new Date().toISOString(),
    groups,
    rows: [],
  };
}


/**
 * Parse Fee Scenarios sheet.
 * Expected columns: Scenario | Group | Label | Constructed Area | Rate | Fee
 * We read Scenario name + Group code + Rate. Area and Fee are recomputed.
 */
function parseScenariosSheet(ws, parsedGroups, errors) {
  let headerRow = 0;
  ws.eachRow((row, rowNum) => {
    if (headerRow > 0) return;
    const a = cellStr(row.getCell(1)).toLowerCase();
    if (a === 'scenario') headerRow = rowNum;
  });

  if (headerRow === 0) {
    errors.push('Fee Scenarios: Could not find header row (expected "Scenario" in column A).');
    return null;
  }

  // Rates are per unit area, so read the unit off the area/rate headers and
  // convert back to the canonical ₹/ft².
  const sheetUnit = detectUnitFromHeaders(ws, headerRow, [4, 5]);
  const toCanonicalRate = (v) => (sheetUnit === 'm2' ? ratePerM2ToPerSqft(v) : (parseFloat(v) || 0));

  // Build group code → id map from parsed area groups
  const groupCodeToId = new Map();
  (parsedGroups || []).forEach(g => groupCodeToId.set(g.code, g.id));

  const scenariosMap = new Map(); // name → { name, groupRates }
  const scenarioOrder = [];

  const totalRows = ws.rowCount;
  for (let r = headerRow + 1; r <= totalRows; r++) {
    const row = ws.getRow(r);
    const colA = cellStr(row.getCell(1));
    const colB = cellStr(row.getCell(2));  // group code
    const rateSqft = toCanonicalRate(cellNum(row.getCell(5)));

    // Skip total/empty rows
    if (!colA || colA.toLowerCase().includes('total')) continue;
    if (!colB) continue;

    const scenarioName = colA;
    if (!scenariosMap.has(scenarioName)) {
      scenariosMap.set(scenarioName, { name: scenarioName, groupRates: {} });
      scenarioOrder.push(scenarioName);
    }

    const groupId = groupCodeToId.get(colB);
    if (!groupId) {
      errors.push(`Fee Scenarios: Unknown group code "${colB}" in scenario "${scenarioName}".`);
      continue;
    }

    if (rateSqft > 0) {
      scenariosMap.get(scenarioName).groupRates[groupId] = rateSqft;
    }
  }

  return scenarioOrder.map(name => scenariosMap.get(name));
}


/**
 * Parse Offers sheet.
 * Expected columns: Offer | Group | Label | Constructed Area | Rate | Fee | CC | Date | Notes
 */
function parseOffersSheet(ws, parsedGroups, errors) {
  let headerRow = 0;
  ws.eachRow((row, rowNum) => {
    if (headerRow > 0) return;
    const a = cellStr(row.getCell(1)).toLowerCase();
    if (a === 'offer') headerRow = rowNum;
  });

  if (headerRow === 0) {
    errors.push('Offers: Could not find header row (expected "Offer" in column A).');
    return null;
  }

  const sheetUnit = detectUnitFromHeaders(ws, headerRow, [4, 5]);
  const toCanonicalRate = (v) => (sheetUnit === 'm2' ? ratePerM2ToPerSqft(v) : (parseFloat(v) || 0));

  const groupCodeToId = new Map();
  (parsedGroups || []).forEach(g => groupCodeToId.set(g.code, g.id));

  const offersMap = new Map();
  const offerOrder = [];

  const totalRows = ws.rowCount;
  for (let r = headerRow + 1; r <= totalRows; r++) {
    const row = ws.getRow(r);
    const colA = cellStr(row.getCell(1));
    const colB = cellStr(row.getCell(2));
    const rateSqft = toCanonicalRate(cellNum(row.getCell(5)));
    const cc = cellStr(row.getCell(7));
    const date = cellStr(row.getCell(8));
    const notes = cellStr(row.getCell(9));

    if (!colA || colA.toLowerCase().includes('total')) continue;
    if (!colB) continue;

    const offerLabel = colA;
    if (!offersMap.has(offerLabel)) {
      offersMap.set(offerLabel, { label: offerLabel, groupRates: {}, rows: [], cc: '', date: '', notes: '' });
      offerOrder.push(offerLabel);
    }

    const offer = offersMap.get(offerLabel);

    // Metadata from first row of each offer
    if (cc && !offer.cc) offer.cc = cc;
    if (date && !offer.date) offer.date = date;
    if (notes && !offer.notes) offer.notes = notes;

    const groupId = groupCodeToId.get(colB);
    if (!groupId) {
      errors.push(`Offers: Unknown group code "${colB}" in offer "${offerLabel}".`);
      continue;
    }

    if (rateSqft > 0) {
      offer.groupRates[groupId] = rateSqft;
    }
  }

  return offerOrder.map(label => offersMap.get(label));
}


// ── Browser download helper ─────────────────────────────────────────
/**
 * Trigger browser download of an ArrayBuffer as .xlsx
 */
export function downloadXlsxBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'export.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
